import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getToken, deleteToken } from '../../services/auth';
import { createProductInventory, getBaseProducts } from '../../api/inventories';
import { Button } from '../../constants/base';
import LogoShapya from '../../assets/Logo_ShapYa_Red.png';
import Divider from '@material-ui/core/Divider';
import {
  Box,
  FormControlLabel,
  Typography,
  Container,
  Paper,
  TextField,
} from '@material-ui/core';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputLabel from '@material-ui/core/InputLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormControl from '@material-ui/core/FormControl';
import Switch from '@material-ui/core/Switch';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { ContentWrapper } from '../../shared/ContentWrapper/ContentWrapper';

export const AddProduct = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const storeId = useSelector((state) => state.store.id);

  const [priceValue, setPriceValue] = useState('');
  const [nameValue, setNameValue] = useState('');
  const [productIdValue, setProductIdValue] = useState(null);
  const [bestSellingValue, setBestSellingValue] = useState(false);
  const [activeValue, setActiveValue] = useState(true);
  const [skuValue, setSkuValue] = useState('');
  const [descriptionValue, setDescriptionValue] = useState('');
  const [discountValue, setDiscountValue] = useState(0);

  const [baseProducts, setBaseProducts] = useState([]);
  const [selectedBaseProduct, setSelectedBaseProduct] = useState(null);
  const [disable, setDisable] = useState(false);
  const [apiError, setApiError] = useState(null);

  const fetchBaseProducts = async (searchWord = '') => {
    try {
      const response = await getBaseProducts(getToken(), searchWord);
      const data = response.results || response;
      if (Array.isArray(data)) {
        setBaseProducts(data);
      }
    } catch (error) {
      console.error('Error fetching base products', error);
    }
  };

  useEffect(() => {
    fetchBaseProducts();
  }, []);

  const handleAddProduct = async () => {
    try {
      setDisable(true);
      setApiError(null);

      const createData = {
        id: storeId,
        token: getToken(),
        params: {
          product_id: productIdValue ? productIdValue : '',
          price: priceValue,
          discount: discountValue,
          sku: skuValue,
          name: nameValue,
          description: descriptionValue === '' ? 'No description' : descriptionValue,
          best_selling: bestSellingValue,
          active: activeValue,
        },
      };

      await createProductInventory(createData);
      navigate('/inventory');
    } catch (error) {
      console.log(error);
      setDisable(false);

      if (error.response && error.response.data) {
        if (error.response.status === 401) {
          deleteToken();
          return;
        }

        const errorData = error.response.data;
        const errorKey = Object.keys(errorData)[0];
        const errorMessage = errorData[errorKey][0] || errorData[errorKey];

        setApiError(errorMessage || 'An unknown validation error occurred.');
      } else {
        setApiError('A network error occurred. Please try again.');
      }
    }
  };

  const handleFieldChange = (setter) => (e) => {
    if (apiError) setApiError(null);
    setter(e.target.value);
  };

  const handleCancel = () => {
    navigate('/inventory');
  };

  return (
    <ContentWrapper>
      <Container maxWidth="md" style={{ marginTop: '40px', paddingBottom: '40px' }}>
        <Paper elevation={3} style={{ padding: '30px', borderRadius: '10px' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <img src={LogoShapya} width="120" height="36" alt="Shapya Logo" />
            <Typography variant="h5" component="h2">
              {t('Inventory.modal.productBasicInfoAdd', 'Add New Product')}
            </Typography>
          </Box>
          <Divider />

          <Box mt={4} mb={3}>
            <Typography variant="subtitle1" gutterBottom>
              {t('Inventory.modal.productBasicInfoAdd.subtitle', 'Fill in the basic product details')}
            </Typography>
            <Box mt={2} p={2} style={{ backgroundColor: '#e3f2fd', borderRadius: '8px', border: '1px solid #90caf9' }}>
              <Typography variant="body2" style={{ color: '#0d47a1' }}>
                <strong>Tip:</strong> You can search for an existing product in the <strong>Search Base Product</strong> field. 
                If you find one, select it! We will automatically fill your fields and show its original details above each field. 
                You can still modify these details for your store before saving. 
                If you don't find it, just fill in the details below to create a completely new product.
              </Typography>
            </Box>
          </Box>

          <Box display="grid" gridTemplateColumns="1fr 1fr" gridGap="20px">
            <FormControl fullWidth variant="outlined" style={{ gridColumn: 'span 2' }}>
              <Autocomplete
                options={baseProducts}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.name || '')}
                onChange={(event, newValue) => {
                  if (apiError) setApiError(null);
                  if (newValue && newValue.name) {
                    setSelectedBaseProduct(newValue);
                    setProductIdValue(newValue.id);
                    setNameValue(newValue.name);
                    if (newValue.sku) setSkuValue(newValue.sku);
                    if (newValue.price && newValue.price !== false) setPriceValue(newValue.price);
                    if (newValue.description) setDescriptionValue(newValue.description);
                  } else {
                    setSelectedBaseProduct(null);
                    setProductIdValue(null);
                    setNameValue('');
                    setSkuValue('');
                    setPriceValue('');
                    setDescriptionValue('');
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  if (apiError) setApiError(null);
                  fetchBaseProducts(newInputValue);
                  if (!newInputValue) {
                    setSelectedBaseProduct(null);
                    setProductIdValue(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('Inventory.modal.productBasicInfoUpdate.searchBaseProduct', 'Search Base Product')}
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </FormControl>

            <FormControl fullWidth variant="outlined">
              {selectedBaseProduct && selectedBaseProduct.name && (
                <Typography variant="caption" color="textSecondary" style={{ marginBottom: '4px' }}>
                  Base product value: {selectedBaseProduct.name}
                </Typography>
              )}
              <InputLabel htmlFor="outlined-adornment-name">
                {t('Inventory.modal.productBasicInfoUpdate.inputProductName', 'Product Name')}
              </InputLabel>
              <OutlinedInput
                id="outlined-adornment-name"
                type="text"
                value={nameValue}
                autoComplete="off"
                onChange={(e) => {
                  handleFieldChange(setNameValue)(e);
                  e.target.value === '' || priceValue === '' ? setDisable(true) : setDisable(false);
                }}
                labelWidth={110}
                inputProps={{ maxLength: 120 }}
              />
            </FormControl>

            <FormControl fullWidth variant="outlined">
              {selectedBaseProduct && selectedBaseProduct.sku && (
                <Typography variant="caption" color="textSecondary" style={{ marginBottom: '4px' }}>
                  Base product value: {selectedBaseProduct.sku}
                </Typography>
              )}
              <InputLabel htmlFor="outlined-adornment-sku">
                {t('Inventory.modal.productBasicInfoUpdate.inputProductSku', 'SKU')}
              </InputLabel>
              <OutlinedInput
                id="outlined-adornment-sku"
                type="text"
                value={skuValue}
                autoComplete="off"
                onChange={handleFieldChange(setSkuValue)}
                labelWidth={40}
                inputProps={{ maxLength: 50 }}
              />
            </FormControl>

            <FormControl fullWidth variant="outlined">
              {selectedBaseProduct && selectedBaseProduct.price && selectedBaseProduct.price !== false && (
                <Typography variant="caption" color="textSecondary" style={{ marginBottom: '4px' }}>
                  Base product value: {selectedBaseProduct.price}
                </Typography>
              )}
              <InputLabel htmlFor="outlined-adornment-amount">
                {t('Inventory.modal.productBasicInfoUpdate.inputProductPrice', 'Price')}
              </InputLabel>
              <OutlinedInput
                id="outlined-adornment-amount"
                type="number"
                inputProps={{ min: 0 }}
                value={priceValue}
                autoComplete="off"
                onChange={(e) => {
                  handleFieldChange(setPriceValue)(e);
                  e.target.value === '' || nameValue === '' ? setDisable(true) : setDisable(false);
                }}
                startAdornment={<InputAdornment position="start">$</InputAdornment>}
                labelWidth={50}
              />
            </FormControl>

            <FormControl fullWidth variant="outlined" style={{ gridColumn: 'span 2' }}>
              {selectedBaseProduct && selectedBaseProduct.description && (
                <Typography variant="caption" color="textSecondary" style={{ marginBottom: '4px' }}>
                  Base product value: {selectedBaseProduct.description}
                </Typography>
              )}
              <InputLabel htmlFor="outlined-adornment-description">
                {t('Inventory.modal.productBasicInfoUpdate.inputProductDescription', 'Description')}
              </InputLabel>
              <OutlinedInput
                id="outlined-adornment-description"
                type="text"
                multiline
                rows={3}
                value={descriptionValue}
                autoComplete="off"
                onChange={handleFieldChange(setDescriptionValue)}
                labelWidth={100}
                inputProps={{ maxLength: 520 }}
              />
            </FormControl>

            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="outlined-adornment-discount">
                {t('Inventory.modal.productBasicInfoUpdate.inputProductDiscountPercent', 'Discount %')}
              </InputLabel>
              <OutlinedInput
                id="outlined-adornment-discount"
                type="number"
                inputProps={{ min: 0, max: 99 }}
                value={discountValue}
                autoComplete="off"
                onChange={handleFieldChange(setDiscountValue)}
                endAdornment={<InputAdornment position="end">%</InputAdornment>}
                labelWidth={100}
              />
            </FormControl>
          </Box>

          <Box mt={4} display="flex" flexDirection="column" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={activeValue}
                  onChange={() => setActiveValue(!activeValue)}
                />
              }
              label={
                <Typography variant="subtitle1">
                  {t('Inventory.modal.productBasicInfoUpdate.switchProductAvailable', 'Available')}
                </Typography>
              }
              labelPlacement="end"
            />
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={bestSellingValue}
                  onChange={() => setBestSellingValue(!bestSellingValue)}
                />
              }
              label={
                <Typography variant="subtitle1">
                  {t('Inventory.modal.productBasicInfoUpdate.switchIsBestSelling', 'Best Selling')}
                </Typography>
              }
              labelPlacement="end"
            />
          </Box>

          {apiError && (
            <Box mt={3} p={2} style={{ backgroundColor: 'rgba(211, 47, 47, 0.1)', borderRadius: '4px', width: '100%' }}>
              <Typography color="error" variant="body2">
                {apiError}
              </Typography>
            </Box>
          )}

          <Box mt={4}>
            <Typography variant="subtitle1" color="textSecondary" align="center">
              {t('Inventory.modal.productBasicInfoAddConfirmation', 'Are you sure you want to add this product?')}
            </Typography>
          </Box>

          <Box mt={3} display="flex" justifyContent="center" gap={2}>
            <Button onClick={handleCancel} style={{ marginRight: '16px', backgroundColor: '#e0e0e0', color: '#333' }}>
              {t('Inventory.modal.productBasicInfoUpdate.button.cancel', 'Cancel')}
            </Button>
            <Button disabled={disable} onClick={handleAddProduct} style={{ backgroundColor: '#2e7d32', color: '#fff' }}>
              {t('Inventory.modal.productBasicInfoAdd.button.add', 'Add Product')}
            </Button>
          </Box>
        </Paper>
      </Container>
    </ContentWrapper>
  );
};
